import ClearIcon from '@mui/icons-material/Clear';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { Dispatch, FunctionComponent, SetStateAction } from 'react';
import { useAppTranslate } from '../../../core/hooks';
import { L3ServiceStatus, Preference } from '../../../l3-services/model';
import { Card } from '../../../ui/components';
import { L3ServiceLocationViewModel } from '../../model';
import { TopologyFiltersSectionHeader } from '../TopologyFiltersSectionHeader/TopologyFiltersSectionHeader';
import { TopologyL3ServiceStatusPoint } from '../TopologyL3ServiceStatusPoint/TopologyL3ServiceStatusPoint';

export enum TopologyMaintenanceStatusFilter {
  Upcoming = 'UPCOMING',
  Ongoing = 'ONGOING',
  Without = 'WITHOUT'
}

export enum TopologyNotificationStatusFilter {
  With = 'WITH',
  Without = 'WITHOUT'
}

export interface TopologyFilterValues {
  selectedLocation: L3ServiceLocationViewModel | null;
  serviceStatus: L3ServiceStatus[];
  maintenanceStatus: TopologyMaintenanceStatusFilter[];
  preferenceStatus: Preference[];
  notificationStatus: TopologyNotificationStatusFilter[];
}

interface Props {
  closeFilters: () => void;
  clearFilters: () => void;
  filters: TopologyFilterValues;
  updateFilters: Dispatch<SetStateAction<TopologyFilterValues>>;
}

export const TopologyFilters: FunctionComponent<Props> = ({ closeFilters, clearFilters, filters, updateFilters }) => {
  const translate = useAppTranslate();
  const primaryColor = useTheme().palette.primary.main;

  const updateServiceStatus = (values: L3ServiceStatus[]): void => updateFilters(previous => ({
    ...previous,
    serviceStatus: values,
  }));
  
  const updateMaintenanceStatus = (status: TopologyFilterValues['maintenanceStatus'][number]): void => updateFilters(filters => ({
    ...filters,
    maintenanceStatus: filters.maintenanceStatus.includes(status)
      ? filters.maintenanceStatus.filter(value => value !== status)
      : [...filters.maintenanceStatus, status],
  }));
  
  const updatePreferenceStatus = (status: TopologyFilterValues['preferenceStatus'][number]): void => updateFilters(filters => ({
    ...filters, preferenceStatus: filters.preferenceStatus.includes(status)
      ? filters.preferenceStatus.filter(value => value !== status)
      : [...filters.preferenceStatus, status],
  }));
  
  const updateNotificationStatus = (status: TopologyFilterValues['notificationStatus'][number]): void => updateFilters(filters => ({
    ...filters, notificationStatus: filters.notificationStatus.includes(status)
      ? filters.notificationStatus.filter(value => value !== status)
      : [...filters.notificationStatus, status],
  }));

  return <Card>
    <Box sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* empty box below is here intentionally, it's required for working flex positioning as desired */}
        <Box></Box>
        <Box>{translate('filters.titles.filters')}</Box>
        <Box>
          <IconButton
            onClick={() => {
              closeFilters();
            }}
          >
            <ClearIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
          pt: 1,
          color: primaryColor,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            cursor: 'pointer',
            color: primaryColor,
          }}
          onClick={() => {
            clearFilters();
            closeFilters();
          }}
        >
          <FilterAltOffIcon />
          <span>{translate('filters.titles.clearFilters')}</span>
        </Box>
      </Box>
      <Box>
        <Box>
          <TopologyFiltersSectionHeader title={translate('filters.titles.serviceStatus')} />
          <ToggleButtonGroup
            size={'large'}
            exclusive={false}
            sx={{ background: 'transparent', flexWrap: 'wrap', gap: 2 }}
            value={filters.serviceStatus}
            onChange={(event, values) => {
              updateServiceStatus(values);
            }}
          >
            {Object.values(L3ServiceStatus).map(status => <ToggleButton
              key={status}
              value={status}
              sx={{
                border: '1px #D9D9D9 solid !important',
                '&.Mui-selected': {
                  border: '1px #3253E4 solid !important',
                  background: '#3253e40d',
                  color: '#3253E4',
                },
              }}
            >
              <TopologyL3ServiceStatusPoint status={status} />
              <span>{translate(`status.${status}`)}</span>
            </ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
        <Box>
          <TopologyFiltersSectionHeader title={translate('filters.titles.serviceMaintenanceStatus')} />
          <FormGroup>
            {Object.values(TopologyMaintenanceStatusFilter).map(status => <FormControlLabel
              key={status}
              control={<Checkbox
                checked={filters.maintenanceStatus.includes(status)}
                value={status}
                onChange={() => updateMaintenanceStatus(status)}
              />}
              label={translate(`filters.maintenanceStatus.${status}`)}
            />)}
          </FormGroup>
        </Box>
        <Box>
          <TopologyFiltersSectionHeader title={translate('filters.titles.preferenceStatus')} />
          <FormGroup>
            <FormControlLabel
              control={<Checkbox
                checked={filters.preferenceStatus.includes(Preference.Favourite)}
                value={Preference.Favourite}
                onChange={() => updatePreferenceStatus(Preference.Favourite)}
              />}
              label={translate(`filters.preferenceStatus.${Preference.Favourite}`)}
            />
          </FormGroup>
        </Box>
        <Box>
          <TopologyFiltersSectionHeader title={translate('filters.titles.activeNotifications')} />
          <FormGroup>
            {Object.values(TopologyNotificationStatusFilter).map(status => <FormControlLabel
              key={status}
              control={<Checkbox
                checked={filters.notificationStatus.includes(status)}
                value={status}
                onChange={() => updateNotificationStatus(status)}
              />}
              label={translate(`filters.activeNotificationsStatus.${status}`)}
            />)}
          </FormGroup>
        </Box>
      </Box>
    </Box>
  </Card>;
};